export const THEMES = [
  {
    id: 'extra',
    name: 'Extra',
    description: 'Every star starts somewhere.',
    streakRequired: 0,
    unlocked: true,
  },
  {
    id: 'understudy',
    name: 'Understudy',
    description: 'Learning the craft.',
    streakRequired: 7,
  },
  {
    id: 'supporting-role',
    name: 'Supporting Role',
    description: 'Making your mark.',
    streakRequired: 7,
  },
  {
    id: 'leading-role',
    name: 'Leading Role',
    description: 'Your name above the title.',
    streakRequired: 7,
  },
  {
    id: 'award-season',
    name: 'Award Season',
    description: 'The industry is watching.',
    streakRequired: 7,
  },
  {
    id: 'a-list',
    name: 'A-List',
    description: 'The premiere awaits.',
    streakRequired: 7,
  },
  {
    id: 'oscar-night',
    name: 'Oscar Night',
    description: 'You made it.',
    streakRequired: 7,
  },
]

/** Price (in tickets) to permanently unlock any locked theme. */
export const THEME_PRICE_TICKETS = 100

/**
 * Return the list of theme IDs unlocked for a given streak (or all if admin).
 *
 * NOTE: Under the new permanent-unlock system, this is NOT used to compute
 * the player's actual unlocked themes — that comes from
 * `permanentlyUnlockedThemes` state. This helper remains for two reasons:
 *   - admin path (still wants every theme)
 *   - the one-time backfill at hydrate (see backfillPermFromStreak)
 */
export function getUnlockedThemeIds(streak, isAdmin = false) {
  if (isAdmin) return THEMES.map((t) => t.id)
  return THEMES.filter((t) => streak >= t.streakRequired).map((t) => t.id)
}

/**
 * Return the ID of the next theme that's still locked, in THEMES order.
 * Used by the daily-completion handler when a 7-day milestone is hit — we
 * always award the LOWEST-ordered locked theme, so out-of-order ticket
 * purchases (e.g. buying Award Season first) don't break the cadence —
 * a future streak unlock will fill the gap.
 *
 * Returns null when every theme is already unlocked.
 */
export function getNextLockedThemeId(permanentlyUnlocked = []) {
  const set = new Set(permanentlyUnlocked)
  for (const t of THEMES) {
    if (!set.has(t.id)) return t.id
  }
  return null
}

/**
 * One-time migration helper for existing users. If the user has a non-zero
 * streak but no permanently-unlocked themes recorded yet (because the
 * permanent-unlock system didn't exist when they earned those streak days),
 * seed their perm list with up to floor(streak/7) themes in THEMES order so
 * they don't lose visible access to themes they already earned under the
 * old derive-from-streak logic.
 *
 * Idempotent and conservative: returns `existing` unchanged when the user
 * has already accumulated >1 perm theme, OR when they have no streak yet.
 */
export function backfillPermFromStreak(existing = [], currentStreak = 0) {
  const merged = new Set(existing)
  merged.add('extra') // baseline guarantee
  if (merged.size > 1) return Array.from(merged)         // already has unlocks → don't touch
  if (!currentStreak || currentStreak < 7) return Array.from(merged)
  const seedCount = Math.min(Math.floor(currentStreak / 7), THEMES.length - 1)
  for (let i = 1; i <= seedCount; i++) merged.add(THEMES[i].id)
  return Array.from(merged)
}

/** Check admin flag stored in localStorage. */
export function isAdminUser() {
  return localStorage.getItem('action_admin') === 'true'
}

const THEME_STORAGE_KEY = 'action_theme'
const UNLOCKED_STORAGE_KEY = 'action_unlocked_themes'

export function getSavedTheme() {
  return localStorage.getItem(THEME_STORAGE_KEY) || 'extra'
}

export function persistTheme(id) {
  localStorage.setItem(THEME_STORAGE_KEY, id)
}

export function persistUnlockedThemes(ids) {
  localStorage.setItem(UNLOCKED_STORAGE_KEY, JSON.stringify(ids))
}

export function getSavedUnlockedThemes() {
  try {
    const raw = localStorage.getItem(UNLOCKED_STORAGE_KEY)
    return raw ? JSON.parse(raw) : ['extra']
  } catch {
    return ['extra']
  }
}
